import React from 'react';
import { Card, List, Avatar, Typography, Tag, Button } from 'antd';
import { 
    TrophyOutlined, 
    DollarOutlined, 
    CalendarOutlined,
    RightOutlined,
    CrownOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface TopPayingCompaniesProps {
    companies: {
        companyName: string;
        totalPaid: number;
        lastPayment: string;
        companyId?: number;
        planType?: string;
        transactionCount?: number;
    }[];
    loading?: boolean;
}

const TopPayingCompanies: React.FC<TopPayingCompaniesProps> = ({ companies, loading = false }) => {
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getRankIcon = (index: number) => {
        switch (index) {
            case 0:
                return <CrownOutlined style={{ color: '#FFD700', fontSize: '18px' }} />;
            case 1:
                return <TrophyOutlined style={{ color: '#C0C0C0', fontSize: '16px' }} />;
            case 2:
                return <TrophyOutlined style={{ color: '#CD7F32', fontSize: '16px' }} />;
            default:
                return <span className="fw-bold text-muted">#{index + 1}</span>;
        }
    };

    const getRankColor = (index: number) => {
        switch (index) {
            case 0: return '#FFD700';
            case 1: return '#C0C0C0';
            case 2: return '#CD7F32';
            default: return '#f0f0f0';
        }
    };

    const getPlanColor = (planType: string) => {
        switch (planType?.toLowerCase()) {
            case 'premium': return 'gold';
            case 'enterprise': return 'purple';
            case 'basic': return 'blue';
            default: return 'default';
        }
    };

    return (
        <Card 
            className="border-0 shadow-sm"
            loading={loading}
            title={
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <div className="me-2 p-2 bg-warning bg-opacity-10 rounded">
                            <TrophyOutlined className="text-warning" />
                        </div>
                        <div>
                            <Title level={5} className="mb-0">En Çok Ödeme Yapan Şirketler</Title>
                            <small className="text-muted">Gelir sıralaması ve son ödemeler</small>
                        </div>
                    </div>
                    <Link to="/superadmin/payment-reports">
                        <Button type="text" size="small" icon={<RightOutlined />}>
                            Tümünü Gör
                        </Button>
                    </Link>
                </div>
            }
            bodyStyle={{ padding: '0' }}
        >
            <List
                itemLayout="horizontal"
                dataSource={companies}
                renderItem={(company, index) => (
                    <List.Item
                        className="px-4 py-3 border-0"
                        style={{ 
                            backgroundColor: index < 3 ? `${getRankColor(index)}10` : 'transparent',
                            borderLeft: index < 3 ? `3px solid ${getRankColor(index)}` : 'none'
                        }}
                        actions={[
                            <div className="text-end">
                                <div className="fw-bold text-success mb-1">
                                    {formatCurrency(company.totalPaid)}
                                </div>
                                <div className="small text-muted">
                                    <CalendarOutlined className="me-1" />
                                    {dayjs(company.lastPayment).format('DD.MM.YYYY')}
                                </div>
                            </div>
                        ]}
                    >
                        <List.Item.Meta
                            avatar={
                                <div className="position-relative">
                                    <Avatar 
                                        size={44}
                                        style={{ 
                                            backgroundColor: getRankColor(index),
                                            color: index < 3 ? '#fff' : '#666',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {company.companyName.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <div 
                                        className="position-absolute"
                                        style={{ 
                                            top: '-5px', 
                                            right: '-5px',
                                            backgroundColor: '#fff',
                                            borderRadius: '50%',
                                            padding: '2px',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                        }}
                                    >
                                        {getRankIcon(index)}
                                    </div>
                                </div>
                            }
                            title={
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <Text strong className="d-block">
                                            {company.companyName}
                                        </Text>
                                        <div className="mt-1">
                                            {company.planType && (
                                                <Tag 
                                                    color={getPlanColor(company.planType)} 
                                                    className="me-2"
                                                >
                                                    {company.planType}
                                                </Tag>
                                            )}
                                            {company.transactionCount && (
                                                <Text type="secondary" className="small">
                                                    {company.transactionCount} işlem
                                                </Text>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            }
                        />
                    </List.Item>
                )}
            />

            {/* Alt bilgi */}
            <div className="px-4 py-3 bg-light border-top">
                <div className="row text-center">
                    <div className="col-4">
                        <div className="d-flex align-items-center justify-content-center">
                            <CrownOutlined className="text-warning me-1" />
                            <small className="text-muted">1. Sıra</small>
                        </div>
                    </div>
                    <div className="col-4">
                        <div className="d-flex align-items-center justify-content-center">
                            <DollarOutlined className="text-success me-1" />
                            <small className="text-muted">Toplam Gelir</small>
                        </div>
                    </div>
                    <div className="col-4">
                        <small className="text-muted">
                            {companies.length} şirket
                        </small>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default TopPayingCompanies; 